import React from 'react';
import { ProductSubType, ProductType, TransactionStatus } from '@ace-clublabs/payment-types';
import { IGetConfirmation, IGetExpiredConfirmation, IGetConfirmationResponse } from '@lib/types';
import { createAccountOnClick, loginOnClick } from '../utils/methods';
import { ClubHelper, PolicyHelper } from '@ace-clublabs/payments-helper';
import { aceClubs } from '../shared/constants';
import { Confirmation as PCLConfirmation } from '@ace-clublabs/payments-component-library';
import { Typography, Link } from '@ace-clublabs/shared-component-library';

type Props = IGetConfirmationResponse & {
  clubCode: string;
};

const Confirmation: React.FC<Props> = (props) => {
  const {
    primaryEmail,
    isEmailOnFile,
    secondaryEmail,
    firstName,
    amountPaid,
    paymentStatus,
    autoPay,
    confirmationNumber,
    policyNumber,
    paymentType,
    last4,
    brand,
    routingNumber,
  } = props as IGetConfirmation;

  const { expired } = props as IGetExpiredConfirmation;
  const clubCode = props.clubCode;
  const isAceClub = aceClubs.find((ac: string) => ac === clubCode);
  const phoneNumber = ClubHelper.getClubPhoneNumber(clubCode);

  const handleLoginClick = () => loginOnClick();

  const createAccountHandler = isAceClub ? createAccountOnClick : undefined;

  const autoPayFailed = autoPay?.status && autoPay?.status !== TransactionStatus.Succeeded;
  const autoPaySucceeded = autoPay?.status && autoPay?.status === TransactionStatus.Succeeded;
  const isStandaloneAutoPay = !amountPaid && autoPay?.status === TransactionStatus.Succeeded;

  const headerText = isStandaloneAutoPay
    ? 'Thank you for enrolling in Auto Pay'
    : amountPaid && autoPaySucceeded
      ? 'Thank you for your payment and Auto Pay enrollment'
      : 'Thank you for your payment';

  const getAAAVisaOfferText = () => {
    if (['004', '240', '188'].includes(clubCode)) return undefined;

    const title = `Hey ${firstName ? firstName.charAt(0).toUpperCase() + firstName.slice(1).toLowerCase() : 'member'},`;
    return amountPaid && amountPaid * 0.03 > 5
      ? {
          title,
          description: `You could have earned <savingsAmount> cash back using your AAA Visa Signature® credit card at AAA.`,
          linkText: 'Learn more',
          savingsAmount: `${(amountPaid * 0.03).toFixed(2)}`,
        }
      : {
          title,
          description: 'Earn cash back on everyday purchases everywhere using your AAA Visa Signature® credit card.',
          linkText: 'Learn more',
        };
  };

  const productSubType = PolicyHelper.getProductInfoFromPolicyNumber(policyNumber)?.productSubType;

  const getEnabledBanners = (): ProductSubType[] => {
    if (['240', '188'].includes(clubCode)) return [];

    const enabledBanners: ProductSubType[] = [];
    const hasAutoInsurance = productSubType === ProductSubType.Auto;
    const hasHomeInsurance = productSubType === ProductSubType.Home;

    if (!hasAutoInsurance) {
      enabledBanners.push(ProductSubType.Auto);
    } else if (!hasHomeInsurance) {
      enabledBanners.push(ProductSubType.Home);
    }

    return enabledBanners;
  };

  let confirmationProps = {
    title: headerText,
    confirmationDetails: {
      confirmationNumber,
      productId: policyNumber,
      paymentDetails: {
        paymentStatus,
        autoPayStatus: autoPaySucceeded ? TransactionStatus.Succeeded : undefined,
        amountPaid,
        paymentType,
        card: brand ? { brand, last4 } : undefined,
        echeck: routingNumber ? { routingNumber, last4 } : undefined,
      },
      emails: { primaryEmail, secondaryEmail, isEmailOnFile },
    },
    button: isAceClub
      ? {
          primary: { buttonText: 'Log in', onClick: handleLoginClick },
        }
      : undefined,
    displayScheduledPayment: false,
    errorMessage: autoPayFailed ? (
      <>
        Sorry, we encountered an issue and could not complete your Auto Pay enrollment.{' '}
        <Link variant="b2Regular" target="_self" href={`${window.location.origin}/payments/manage-autopay`}>
          Manage Auto Pay
        </Link>
        .
      </>
    ) : undefined,
    autoPayMessage: autoPaySucceeded ? (
      <Typography variant="b2Regular">
        <strong>Auto Pay: Enrolled</strong> — Starting with your next billing cycle, we'll begin automatically charging
        or debiting your selected payment method.
      </Typography>
    ) : undefined,
    phoneNumber,
    productType: ProductType.Insurance,
    discontinueAutoPayMessage: isAceClub && autoPaySucceeded ? true : false,
    minimalPage: expired,
    aaaVisaOffer: getAAAVisaOfferText(),
    createAccountHandler: createAccountHandler,
    showAcquisitionBanner: {
      enabledBanners: getEnabledBanners(),
    },
  };

  return (
    <>
      <div className="confirmation-container" id="confirmation-container">
        <PCLConfirmation
          {...confirmationProps}
          minimalPage={expired}
          aaaVisaOffer={getAAAVisaOfferText()}
          createAccountHandler={createAccountHandler}
          showAcquisitionBanner={{
            enabledBanners: getEnabledBanners(),
          }}
        />
      </div>
    </>
  );
};

export default Confirmation;
